import get from "lodash/get"

import api from "../api.service"
import authService, {getCurrentUserId} from "../auth/auth.service"
import {getCache, setCache} from "../../utils/cache"
import pick from 'lodash/pick';
import {getObjectId} from '../../utils/utils';
import {getProfile} from '../auth/auth.api';
import {getOrgPeople} from '../api/v2/orgPerson';
import {ORG_PERMISSION} from '../org/org.const';
import {MISSION_PERMISSION} from './mission.const';
import {getMissionListWithOrgObservers} from '../../utils/mission';

const queryString = require("query-string")

class MissionApi {
    /**
     * @param mission {Object}
     * @param [options] {Object}
     * @param [options.taskAssign] {Boolean} Defaults to true. If false, does not assign auto-generated tasks to the user creating the mission.
     * @return {Promise<any>}
     */
    createMission(mission, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .post(`/mission?${qs}`, mission)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param [queryOptions]
     * @param [queryOptions.mission] {string|string[]} One or more mission ids to filter the results by
     * @param [queryOptions.myMissions] {boolean} If true returns only the missions where the user is admin, manager or member. Defaults to true.
     * @param [queryOptions.isActive] {boolean} If true returns only active missions. If false, returns only completed missions. If not specified, returns both active and completed.
     * @param [queryOptions.completedBufferDate] {number} Buffer date to return missions as active, even if mission stop is in the past
     * @param [queryOptions.staleDate] {number} Buffer date to return missions as active, even if mission plan end date is in the past
     * @param [queryOptions.startDate] {number} If specified, will return all missions after this date. If "endDate" is specified as well, will return missions within the range.
     * @param [queryOptions.endDate] {number} If specified, will return all missions before this date. If "startDate" is specified as well, will return missions within the range.
     * @param [options] {object}
     * @param [options.cached] {boolean} If true, it will try to return a cached version
     * @param [options.cacheEnabled] {boolean} Enable caching. Defaults to true
     * @return {Promise<string>}
     */
    async getMissionListTree(queryOptions = {}, options = {}) {
        const {cached = false, cacheEnabled = true} = options
        const qs = queryString.stringify(queryOptions)
        const url = `/mission/list/tree?${qs}`

        if (cacheEnabled === true && cached === true) {
            try {
                const {data} = await getCache(url)

                if (data) {
                    return data
                }
            } catch (err) {
                // Nothing
                return
            }
        }

        return api
            .get(url)
            .then(async (res) => {
                let missionList = await getMissionListWithOrgObservers(res.data);

                if (cacheEnabled === true) {
                    setCache(missionList, url).catch((err) => {
                        // Nothing...
                    })
                }

                return missionList
            })
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    streamMissionListTree(queryOptions = {}, options = {}) {
        return this.getMissionListTree({
            ...queryOptions,
            stream: 1
        }, options);
    }

    getGtMyProgramMissionList(orgId, program) {
        const qs = queryString.stringify({
            programTag: program,
        })
        return api
            .get(`/mission/list/gt/${orgId}/program?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateMission(missionId, mission) {
        return api
            .put(`/mission/${missionId}`, mission)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    licenseMission(missionId, licenseKey) {
        return api
            .post(`/mission/${missionId}/license  `, {licenseKey})
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    deleteMission(missionId) {
        return api
            .delete(`/mission/${missionId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @param missionId {string}
     * @param missionPeopleId {string}
     * @param missionPeopleItem {object}
     * @param [options] {object}
     * @param [options.notify] {boolean} Set to "false" to not send any notifications or emails
     * @return {Promise<*>}
     */
    updateMissionPeople(missionId, missionPeopleId, missionPeopleItem, options = {}) {
        const qs = queryString.stringify(options);
        return api
            .put(`/mission/${missionId}/people/${missionPeopleId}?${qs}`, missionPeopleItem)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    deleteMissionPeople(missionId, missionPeopleId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .delete(`/mission/${missionId}/people/${missionPeopleId}?${qs}`)
            .then((res) => {
                return res.data
            })
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    addMissionKpi(missionId, kpi) {
        return api
            .post(`/mission/${missionId}/kpi`, kpi)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateMissionKpi(missionId, kpiId, kpi) {
        return api
            .put(`/mission/${missionId}/kpi/${kpiId}`, kpi)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    deleteMissionKpi(missionId, kpiId) {
        return api
            .delete(`/mission/${missionId}/kpi/${kpiId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    addMissionSectionTag(missionId, sectionTag) {
        return api
            .post(`/mission/${missionId}/section-tag`, sectionTag)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    updateMissionSectionTag(missionId, sectionTagId, sectionTag) {
        return api
            .put(`/mission/${missionId}/section-tag/${sectionTagId}`, sectionTag)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    deleteMissionSectionTag(missionId, sectionTagId) {
        return api
            .delete(`/mission/${missionId}/section-tag/${sectionTagId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     *
     * @param missionId
     * @param inviteeEmail
     * @param data Invitation data
     * @param [options] {object}
     * @param [options.notify] {boolean} If set to false, we don't send any emails or notifications
     */
    createInvite(missionId, inviteeEmail, data, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .post(`/mission/${missionId}/invite?${qs}`, Object.assign({}, data, {inviteeEmail}))
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    async handleInvite(token, payload) {
        const user = await authService.getCurrentUser()

        return (
            api
                .post(`/mission-invite/handle/${token}`, payload)
                // Refresh token in case email was verified during invite handle
                .then(() => user.getIdToken(true))
                .catch((err) => {
                    throw api.getResponseError(err)
                })
        )
    }

    getInviteByToken(token) {
        return api
            .get(`/mission-invite/token/${token}`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * Just marks notification as read and stops displaying to the user.
     * Used in the case that the user is already part of the mission and doesn't need to accept
     * E.g. Permission changed from org observer to member
     * @param inviteId
     * @return {Promise<*>}
     */
    confirmInvite(inviteId) {
        return api
            .put(`/mission-invite/${inviteId}/confirm`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    acceptInvite(inviteId) {
        return api
            .put(`/mission-invite/${inviteId}/accept`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    rejectInvite(inviteId) {
        return api
            .put(`/mission-invite/${inviteId}/reject`)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * @callback onUploadProgress
     * @param {Number} loaded Bytes that have been loaded
     * @param {Number} total The total upload bytes
     */
    /**
     * @param missionId {String}
     * @param files {File|Array<File>}
     * @param [options] {Object}
     * @param [options.onUploadProgress] {onUploadProgress} The progress of the upload
     * @return {Promise<*>}
     */
    uploadFiles(missionId, files, options = {}) {
        return api
            .uploadFileMulti(`/mission/${missionId}/drive`, files, options)
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data") || err
            })
    }

    /**
     * @param missionId {String}
     * @param fileId {String}
     * @param data {Object}
     * @param [data.title] {String} File title
     * @param [data.description] {String} File description
     * @param [data.name] {String} Filename
     * @param [data.acl] {Object} Needs owner permissions
     * @return {Promise<any>}
     */
    updateFile(missionId, fileId, data) {
        return api
            .put(`/mission/${missionId}/drive/${fileId}`, data)
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data") || err
            })
    }

    deleteFile(missionId, fileId) {
        return api
            .delete(`/mission/${missionId}/drive/${fileId}`)
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data") || err
            })
    }

    applyPromo(missionId, promoCode) {
        return api
            .put(`/mission/${missionId}/promo/apply`, {promoCode})
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data") || err
            })
    }

    /**
     * @param missionId
     * @param [options] {Object}
     * @param [options.limit] {String} The limit of results. Defaults to 100.
     * @param [options.page] {String} Paginate results to specified page, starting from page 1 as default.
     * @param [options.sort] {('asc'|'desc')} Sort either ascending or descending (by date). Defaults to desc.
     * @param [options.createdAtFrom] {Date|Number} Date range history was recorded
     * @param [options.createdAtTo] {Date|Number} Date range history was recorded
     * @returns {Promise<any>}
     */
    getHistory(missionId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .get(`/mission/${missionId}/history?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data")
            })
    }

    /**
     * Returns historical of mission, plus all items with "missionId" set to mission id.
     * ONLY mission or org Admins can view this information.
     *
     * @param missionId
     * @param [options] {Object}
     * @param [options.limit] {String} The limit of results. Defaults to 100.
     * @param [options.page] {String} Paginate results to specified page, starting from page 1 as default.
     * @param [options.sort] {('asc'|'desc')} Sort either ascending or descending (by date). Defaults to desc.
     * @param [options.createdAtFrom] {Date|Number} Date range history was recorded
     * @param [options.createdAtTo] {Date|Number} Date range history was recorded
     * @returns {Promise<any>}
     */
    getAuditTree(missionId, options = {}) {
        const qs = queryString.stringify(options)
        return api
            .get(`/mission/${missionId}/audit/tree?${qs}`)
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data")
            })
    }

    /**
     * Charges customer immediately by creating a payment intent.
     * https://stripe.com/docs/payments/intents#intent-statuses
     *
     * Returns an object with payment status.
     * Status is one of these:
     *     'succeeded', // The payment went through
     *     'processing', // Card processing can be quick, other types of payment methods can take up to a few days to process.
     *     'requires_payment_method', // Payment attempt fails (for example due to a decline)
     *     'requires_action', // Such as authenticating with 3D Secure
     *
     * !!!IMPORTANT!!! ====> "requires_action" status is not handled at the moment
     *
     * @param missionId
     * @param idempotency_key To avoid duplicate charges: https://stripe.com/docs/api/idempotent_requests
     * @returns {Promise<*>}
     */
    payProposal(missionId, idempotency_key) {
        return api
            .post(`/mission/${missionId}/proposal/pay`, {idempotency_key})
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data")
            })
    }

    /**
     * @param missionId
     * @returns {Promise<*>}
     */
    payProposalCancel(missionId) {
        return api
            .delete(`/mission/${missionId}/proposal/pay`)
            .then((res) => res.data)
            .catch((err) => {
                throw get(err || {}, "response.data")
            })
    }

    /**
     * Add baseline
     * @param missionId {String}
     * @param payload {Object} The baseline payload
     */
    addBaseline(missionId, payload) {
        return api
            .post(`/mission/${missionId}/baseline`, payload)
            .then((res) => res.data)
            .catch((err) => {
                throw api.getResponseError(err)
            })
    }

    /**
     * EXAMPLE:
     * apiMission.onChanges('update', data => console.log(data))
     * @param operation {string} - update, create or delete
     * @param [query] {Object}
     * @param [cb] {Function}
     */
    async onChanges(operation, query, cb) {
        if (typeof query === "function") {
            cb = query
            query = {}
        }

        const socket = await api.socket.connect(`changes/missions/${operation}`, {
            query,
        })

        socket.on("changes", cb)
    }

    onPeopleChanges(operation, query, cb) {
        if (typeof query === "function") {
            cb = query
            query = {}
        }

        ;(async () => {
            const socket = await api.socket.connect(`changes/mission.people/${operation}`, { query })
            socket.on("changes", cb)
        })()
    }
}

export default new MissionApi()
